import { useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { Pencil, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { SubscriptionForm } from "../components/subscription-form"
import { SubscriptionQuery } from "@/lib/graphql/queries"
import { DeleteSubscriptionMutation } from "@/lib/graphql/mutations"

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, reexecute] = useQuery({ query: SubscriptionQuery, variables: { id: id! } })
  const [, deleteSub] = useMutation(DeleteSubscriptionMutation)

  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const sub = result.data?.subscription

  const handleDelete = async () => {
    if (!id) return
    setDeleting(true)
    const res = await deleteSub({ id })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Subscription deleted")
    navigate("/subscriptions")
  }

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!sub) {
    return <EmptyState title="Subscription not found" />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{sub.name}</h1>
          <Badge variant={sub.enabled ? "default" : "secondary"} className="text-2xs">
            {sub.enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Routing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Source</FieldLabel>
                <Link to={`/sources/${sub.source.id}`} className="hover:underline">
                  {sub.source.name}
                  <span className="text-muted-foreground ml-1">({sub.source.slug})</span>
                </Link>
              </div>
              <div>
                <FieldLabel className="mb-1">Destination</FieldLabel>
                <div className="flex items-center gap-2">
                  <Link to={`/destinations/${sub.destination.id}`} className="hover:underline">
                    {sub.destination.name}
                  </Link>
                  <StatusBadge status={sub.destination.status} />
                </div>
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Event Types</FieldLabel>
              <code className="text-sm font-mono text-muted-foreground">
                {sub.filterConfig?.eventTypes?.join(", ") || "* (all events)"}
              </code>
            </div>
            {sub.filterConfig?.regex && sub.filterConfig.regex.length > 0 && (
              <div>
                <FieldLabel className="mb-1">Regex Patterns</FieldLabel>
                <div className="space-y-1">
                  {sub.filterConfig.regex.map((r) => (
                    <code key={r} className="text-sm font-mono text-muted-foreground block">{r}</code>
                  ))}
                </div>
              </div>
            )}
            {sub.filterConfig?.bodyMatch && sub.filterConfig.bodyMatch.length > 0 && (
              <div>
                <FieldLabel className="mb-1">Body Match Rules</FieldLabel>
                <div className="space-y-1">
                  {sub.filterConfig.bodyMatch.map((rule, i) => (
                    <code key={i} className="text-sm font-mono text-muted-foreground block">
                      {rule.path} {rule.operator} {rule.operator !== "exists" && rule.operator !== "not_exists" ? `"${rule.value}"` : ""}
                    </code>
                  ))}
                </div>
              </div>
            )}
            {sub.filterConfig?.rules && sub.filterConfig.rules.length > 0 && (
              <div>
                <FieldLabel className="mb-1">Advanced Filter Rules</FieldLabel>
                <div className="space-y-1">
                  {sub.filterConfig.rules.map((rule, i) => (
                    <code key={i} className="text-sm font-mono text-muted-foreground block">
                      {rule.path} {rule.operator} {rule.operator !== "$exists" ? JSON.stringify(rule.value) : ""}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{sub.id}</code>
                <CopyButton text={sub.id} />
              </div>
            </div>
            {sub.delay && (
              <div>
                <FieldLabel className="mb-1">Delay</FieldLabel>
                <code className="text-sm font-mono text-muted-foreground">{sub.delay}</code>
              </div>
            )}
            {sub.channels && sub.channels.length > 0 && (
              <div>
                <FieldLabel className="mb-1">Channels</FieldLabel>
                <div className="flex flex-wrap gap-1">
                  {sub.channels.map((ch) => (
                    <Badge key={ch} variant="outline" className="text-2xs font-mono">{ch}</Badge>
                  ))}
                </div>
              </div>
            )}
            {sub.transformation && (
              <div>
                <FieldLabel className="mb-1">Transformation</FieldLabel>
                <Link to={`/transformations`} className="text-sm hover:underline">
                  {sub.transformation.name}
                </Link>
              </div>
            )}
            {sub.transformConfig && (sub.transformConfig.envelope || (sub.transformConfig.headers && Object.keys(sub.transformConfig.headers).length > 0)) && (
              <div>
                <FieldLabel className="mb-1">Transform Config</FieldLabel>
                <div className="space-y-1">
                  {sub.transformConfig.envelope && (
                    <Badge variant="outline" className="text-2xs">Envelope wrapping</Badge>
                  )}
                  {sub.transformConfig.headers && Object.keys(sub.transformConfig.headers).length > 0 && (
                    <code className="text-xs font-mono text-muted-foreground block">
                      {JSON.stringify(sub.transformConfig.headers)}
                    </code>
                  )}
                </div>
              </div>
            )}
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={sub.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <SubscriptionForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        editId={id}
        defaultValues={{
          name: sub.name,
          sourceId: sub.sourceId,
          destinationId: sub.destinationId,
          eventTypes: sub.filterConfig?.eventTypes?.join(", ") ?? "",
          regex: sub.filterConfig?.regex?.join("\n") ?? "",
          bodyMatchRules: sub.filterConfig?.bodyMatch?.map((r) => ({ path: r.path, operator: r.operator as "eq" | "neq" | "contains" | "exists" | "not_exists", value: r.value })) ?? [],
          filterRules: sub.filterConfig?.rules?.map((r) => ({ path: r.path ?? "", operator: (r.operator ?? "$eq") as "$eq" | "$neq" | "$gt" | "$gte" | "$lt" | "$lte" | "$in" | "$nin" | "$regex" | "$exists", value: r.value != null ? String(r.value) : "" })) ?? [],
          envelope: sub.transformConfig?.envelope ?? false,
          transformHeaders: sub.transformConfig?.headers ? JSON.stringify(sub.transformConfig.headers) : "",
          transformationId: sub.transformationId ?? "",
          enabled: sub.enabled,
          delay: sub.delay ?? "",
          channels: sub.channels?.join(", ") ?? "",
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Subscription"
        description="This will permanently delete this subscription. Events will no longer be routed through it."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />
    </div>
  )
}
